import { AsyncPipe, CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, output, signal, Type } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { L10N_LOCALE, L10nTranslatePipe, L10nTranslationService } from 'angular-l10n';
import { Subscription, take } from 'rxjs';
import { FileModel, FilesApi } from 'src/app/hickory-api';
import { AuthService } from 'src/app/shared/authentication/auth.service';
import { ConfirmComponent } from 'src/app/shared/modals/confirm/confirm.component';
import { getFilesFromInputTarget } from 'src/app/shared/model/utility';
import { FileDetailsComponent } from '../file-details/file-details.component';
import { FileDeleteJob, FileSelectedEvent, FilesViewConstants, FilesViewContext } from '../file-view-context';
import { FilesDataSource } from '../files-datasource';
import { FilesTableComponent } from '../files-table/files-table.component';
import { FileModelHelper, FileModelView } from '../file-model-helper';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-file-management',
  imports: [
    AsyncPipe,
    CommonModule,
    L10nTranslatePipe,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatProgressBarModule,
    MatToolbarModule,
    MatTooltipModule,
    RouterLink
  ],
  templateUrl: './file-management.component.html',
  styleUrl: './file-management.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileManagementComponent implements OnInit, OnDestroy {
  public readonly locale = inject(L10N_LOCALE);
  private readonly filesApi = inject(FilesApi);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly dialog = inject(MatDialog);
  private readonly translation = inject(L10nTranslationService);
  private readonly snackBar = inject(MatSnackBar);

  protected readonly deleteButton = FilesViewConstants.DELETE_BUTTON;

  private readonly sub = new Subscription();

  protected get currentPath() {
    return this.router.url.split('?')[0];
  }

  protected readonly viewContext: FilesViewContext = new FilesViewContext(
    this.route.snapshot.queryParamMap,
    this.currentPath,
    this.auth.isSuperAdmin()
  );

  public datasource: FilesDataSource;

  protected sortField = signal<keyof FileModel | null>(null);
  protected sortDirection = signal<'asc' | 'desc' | null>(null);
  protected fileDescription = new FormControl('');
  public fileResize = new FormControl<number>(1000, [Validators.min(1)]);
  protected fileSearch = new FormControl('', [Validators.minLength(3)]);

  public fileSelected = output<FileSelectedEvent | null>();
  public closeClicked = output<void>();

  protected selectedFile = signal<FileModel | null>(null);
  protected fileToDisplay = computed<FileModelView | null>(() => {
    if (this.fileToUpload()) return this.fileToUpload();
    return FileModelHelper.applyFileViewOverrides(this.selectedFile(), false, this.viewContext.isManagementView());
  });

  get viewOutput(): { component: Type<any>, inputs: Record<string, unknown> } {
    const inputs: Record<string, unknown> = {
      viewContext: this.viewContext,
      datasource: this.datasource,
      fileSearch: this.fileSearch
    };

    if (this.viewContext.displayType() === 'table') {
      inputs['deleteJob'] = this.deleteJob();
      return { component: FilesTableComponent, inputs }
    } else {
      inputs['fileDescription'] = this.fileDescription;
      inputs['fileResize'] = this.fileResize;
      inputs['selectedFile'] = this.fileToDisplay();
      inputs['fileToImport'] = this.fileToUpload();
      return { component: FileDetailsComponent, inputs }
    }
  }

  private getQueryParamsWithNullFileId(params: Record<string, unknown>) {
    return this.viewContext.toQueryParams({ ...params, fileId: null });
  }

  public ngOnInit(): void {
    this.sub.add(
      this.route.queryParamMap.subscribe(params => {
        // this is to handle the case where we navigate to the route without any query params
        // but only from the management view. In this case we want to set the default query params
        if ((params.keys?.length ?? 0) === 0 && this.viewContext.isManagementView()) {
          // if there are no query params set, add them. Updating the query params will trigger this method again
          // but since the params will now be set, it will hit the else block and load data as expected
          // we do this to ensure there is always a consistent URL for the current view
          this.updateQueryParamsForRoute();
        } else {
          const prevParams = this.viewContext.toInterface();
          this.viewContext.updateFromParams(params, this.currentPath);
          this.fileSearch.setValue(this.viewContext.searchTerm() ?? '');
          this.onLoad(!this.datasource?.data?.length || this.viewContext.shouldReload(prevParams));
        }
      })
    );

    this.sub.add(this.viewContext.searchFiles.subscribe(_ => this.onSearch()));
    this.sub.add(this.viewContext.clearSearch.subscribe(_ => this.onClearSearch()));
    this.sub.add(this.viewContext.tenantChanged.subscribe(tenantId => this.onTenantChanged(tenantId)));
    this.sub.add(this.viewContext.useFile.subscribe(_ => this.onUseFile()));
    this.sub.add(this.viewContext.saveFile.subscribe(_ => this.saveFileChanges()));
    this.sub.add(this.viewContext.fileSelected.subscribe(e => this.setSelectedFile(e.FileName)));
    this.sub.add(this.viewContext.deleteSelected.subscribe(() => this.deleteSelectedFiles()));
    this.sub.add(this.viewContext.uploadConfirmed.subscribe(() => this.confirmUpload()));
    this.sub.add(this.viewContext.uploadCancelled.subscribe(() => this.cancelUpload()));
    this.sub.add(this.viewContext.updateQueryParams.subscribe(() => this.updateQueryParamsForRoute()));
  }

  public ngOnDestroy(): void {
    this.datasource?.unsubscribe();
    this.sub.unsubscribe();
  }

  protected onLoad(shouldLoad: boolean = true) {
    if (shouldLoad) {
      this.datasource = new FilesDataSource(this.filesApi, this.viewContext, this.auth);
      this.onViewContextChanges();
    }
  }

  private onViewContextChanges() {
    const request = this.viewContext.toFileListRequest();
      this.datasource.loadDataWithCb(request, _ => {
        if (this.viewContext.displayType() === 'details' && this.viewContext.fileId()) {
          this.setSelectedFile(this.viewContext.fileId()!);
        }
      });
  }

  protected updateQueryParamsForRoute() {
    if (!this.viewContext.isManagementView()) return;
    this.router.navigate([this.currentPath], {
      relativeTo: this.route,
      queryParams: this.viewContext.toQueryParams(),
      onSameUrlNavigation: 'ignore'
    }).catch(() => { });
  }

  // since the data table component gets destroyed, we need to force a router update
  // this will rebuild the datasource for the table view
  protected onSwitchToTableView() {
    return this.getQueryParamsWithNullFileId({ displayType: 'table' });
  }

  // there is no need to reload data when switching to details view
  protected onSwitchToDetailsView() {
    this.viewContext.update({ displayType: 'details' });
  }

  protected onSwitchFileTypes(type: 'image' | 'audio' | null) {
    return this.getQueryParamsWithNullFileId({ fileTypes: type });
  }

  protected onSearch() {
    const term = (this.fileSearch.value as string)?.trim() ?? null;
    this.onApplySearch(term);
  }

  protected onClearSearch() {
    this.fileSearch.setValue(null);
    this.onApplySearch(null);
  }

  private onApplySearch(searchTerm: string | null) {
    // changing the route in a dialog view could lose other changes within the component launching the dialog
    if (this.viewContext.isDialogView()) {
      this.viewContext.update({ searchTerm });
      this.onViewContextChanges();
    } else {
      void this.router.navigate([this.currentPath], {
        relativeTo: this.route,
        queryParams: this.getQueryParamsWithNullFileId({ searchTerm })
      }).catch(() => { });
    }
  }

  protected onClose() {
    this.closeClicked.emit();
  }

  protected onTenantChanged(tenantId: number | null) {
    void this.router.navigate([this.currentPath], {
      relativeTo: this.route,
      queryParams: this.viewContext.toQueryParams({ tenantId })
    }).catch(() => { });
  }

  public fileToUpload = signal<FileModelView | null>(null);
  public uploading = signal<boolean>(false);
  protected async startUpload(target: EventTarget) {
    const fileToUpload = getFilesFromInputTarget(target)[0];
    const fileModel = await FileModelHelper.fileToFileModel(fileToUpload, this.viewContext.isManagementView());
    
    if (!FileModelHelper.fileHasExtension(fileModel)) {
      this.snackBar.open(this.translation.translate('Files.Management.NoExtension'), null, { duration: 5000 });
      this.cancelUpload();
      return;
    }

    this.fileToUpload.set(fileModel);
    this.setFileDescription(null);
    this.onSwitchToDetailsView();
    if (fileToUpload.type.includes('image')) {
      this.fileDescription.markAsDirty();
    }
  }

  protected cancelUpload() {
    const input = document.getElementById('upload-file-input') as HTMLInputElement;
    if (!input) return;
    input.value = '';
    input.files = null;
    this.fileToUpload.set(null);
    this.uploading.set(false);
  }

  protected confirmUpload() {
    const input = document.getElementById('upload-file-input') as HTMLInputElement;
    if (this.fileToUpload() && this.fileDescription.valid) {
      this.uploading.set(true);
      const model = this.fileToUpload()!;
      const file = (input.files as FileList)[0];
      model.Description = this.fileDescription.value;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.filesApi.uploadFileV4(
        this.viewContext.tenantId(),
        file.name,
        file.type,
        file.size,
        file,
        null,
        false,
        false,
        false,
        null,
        this.fileDescription.value
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      ).subscribe({
        next: _ => {
          this.cancelUpload();
          this.viewContext.update({ fileId: file.name });
          this.onLoad(true);
        },
        error: _ => {
          this.cancelUpload();
        }
      });
    }
  }

  protected onUseFile() {
    if (!this.fileToDisplay()) {
      return;
    }

    // if we have an updated description and it doesn't match the selected files description, save it
    if (this.fileDescription.value && this.fileDescription.valid
      && this.fileToDisplay().Description !== this.fileDescription.value) {
      this.saveFileChanges();
    } else {
      // otherwise just emit the selected file
      this.fileSelected.emit(new FileSelectedEvent(this.fileToDisplay()!, this.viewContext.toInterface()));
    }
  }

  protected saveFileChanges() {
    if (this.fileToDisplay() && this.fileDescription.valid) {
      const file = this.fileToDisplay();
      if (file.Description !== this.fileDescription.value) {
        file.Description = this.fileDescription.value;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.filesApi.updateFileDescriptionV4(this.viewContext.tenantId(), file.FileName, this.fileDescription.value)
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .subscribe({
            next: _ => {
              if (!this.viewContext.isManagementView()) {
                this.fileSelected.emit(new FileSelectedEvent(file, this.viewContext.toInterface()));
              } else {
                this.viewContext.update({ fileId: file.FileName });
                this.onLoad(true);
              }
            },
            error: _ => { }
          });
      }
    }
  }

  protected deleteJob = signal<FileDeleteJob | null>(null);
  protected deleteSelectedFiles() {
    const dialogRef = this.dialog?.open(ConfirmComponent, {
      ariaLabel: 'Confirm Delete Files',
      data: {
        title: 'Files.Management.DeleteSelected',
        message: this.translation.translate('Files.Management.DeleteSelectedConfirm', { count: this.datasource.selection.selected.length }),
        ok: 'Files.Management.DeleteSelected',
        okColor: 'warn',
      }
    });
    dialogRef?.afterClosed().pipe(take(1)).subscribe(result => {
      if (result === true && this.datasource.selection.hasValue()) {
        const toDelete = this.datasource.selection.selected.map(f => btoa(f.FileName));
        this.deleteJob.set(new FileDeleteJob(toDelete.length));
        toDelete.forEach(fileId => {
          if (this.deleteJob().progress() >= 100) {
            this.deleteJob.set(null);
            return;
          }
          setTimeout(() => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            this.filesApi.deleteFileV4(this.viewContext.tenantId(), fileId)
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              .subscribe({
                next: _ => this.onDeleteComplete(true, fileId),
                error: _ => this.onDeleteComplete(false, fileId)
              });
          }, 200);
        });
      }
    });
  }

  private onDeleteComplete(isSuccess: boolean, fileId: string) {
    if (this.deleteJob() === null) {
      return;
    }
    if (isSuccess) {
      this.deleteJob().onDeleteSuccess();
    } else {
      this.deleteJob().onDeleteFailed(fileId);
    }
    if (this.deleteJob().progress() >= 100) {
      this.deleteJob.set(null);
      this.onClearSearch();
      this.clearFileSelection();
      this.viewContext.update({ fileId: null });
      this.onLoad(true);
    }
  }

  protected setFileDescription(description: string) {
    this.fileDescription.clearValidators();
    this.fileDescription.setValue(description);
    if (this.isImageFile(this.fileToDisplay())) {
      this.fileDescription.setValidators([Validators.required, Validators.minLength(5)]);
    }
  }

  protected setSelectedFile(filename: string) {
    this.filesApi.getFileV4(this.viewContext.tenantId(), filename).subscribe(fileModel => {
      if (fileModel) {
        const selected = this.datasource.data.filter(f => f.FileName === filename);
        this.datasource.selection.setSelection(...selected);
        this.viewContext.update({ fileId: selected[0]?.FileName ?? null, displayType: 'details' });
        this.selectedFile.set(fileModel ?? null);
        this.setFileDescription(fileModel?.Description ?? '');
      }
    });
  }

  protected clearFileSelection() {
    this.datasource.selection.setSelection(...[]);
    this.selectedFile.set(null);
  }

  protected isImageFile(file: FileModel) {
    return FileModelHelper.isImageFile(file);
  }

  protected getAltText(fileModel: FileModel, defaultAltText = ''): string {
    return FileModelHelper.getAltText(fileModel, defaultAltText);
  }

  protected isSaveOrUploadDisabled() {
    if (!this.fileToDisplay() && !this.fileToUpload()) {
      return true;
    } else {
      return (this.isImageFile(this.fileToDisplay()) || this.isImageFile(this.fileToUpload())) &&
        (this.fileDescription.invalid || !this.fileDescription.value?.trim().length);
    }
  }
}
